import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Card, { CardHeader } from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import Spinner from '../../../components/ui/Spinner'
import Modal from '../../../components/ui/Modal'
import api from '../../../lib/api'
import { formatCurrency, formatTurnaround } from '../../../lib/formatters'
import type { LabProfile } from '../../../types'
import toast from 'react-hot-toast'
import MonthlySalesReport from '../components/MonthlySalesReport'
import MonthlySalesCollectionReport from '../components/MonthlySalesCollectionReport'

type ReportTab = 'overview' | 'monthly-sales' | 'monthly-sales-collection'

// Drill-down modal types
type DrillDownType = 'revenue' | 'orders' | 'collections' | 'turnaround' | 'doctor' | 'category' | null

interface DrillDownData {
  type: DrillDownType
  title: string
  doctorId?: string
  category?: string
}

const REPORT_TABS: { key: ReportTab; label: string; icon: string }[] = [
  { key: 'overview', label: 'Overview', icon: 'fa-chart-pie' },
  { key: 'monthly-sales', label: 'Monthly Sales', icon: 'fa-chart-line' },
  { key: 'monthly-sales-collection', label: 'Sales & Collection', icon: 'fa-file-invoice-dollar' },
]

const getPeriodLabel = (period: string) => {
  const labels: Record<string, string> = {
    this_month: 'This Month',
    last_month: 'Last Month',
    this_quarter: 'This Quarter',
    this_year: 'This Year',
  }
  return labels[period] || period
}

interface ReportData {
  revenue: {
    thisMonth: number
    lastMonth: number
    thisYear: number
    growth: number
  }
  orders: {
    total: number
    completed: number
    pending: number
    avgTurnaround: number
  }
  collections: {
    thisMonth: number
    outstanding: number
    overdue: number
  }
  topDoctors: Array<{
    id: string
    name: string
    clinicName: string
    revenue: number
    orders: number
  }>
  categoryBreakdown: Array<{
    category: string
    revenue: number
    count: number
  }>
}

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<ReportTab>('overview')
  const [period, setPeriod] = useState('this_month')
  const [isExporting, setIsExporting] = useState(false)
  const [drillDown, setDrillDown] = useState<DrillDownData | null>(null)

  // Close drill-down modal
  const closeDrillDown = () => setDrillDown(null)

  const handleExport = async () => {
    try {
      setIsExporting(true)
      const response = await api.get(`/reports/export?period=${period}`, {
        responseType: 'blob',
      })

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `report_${period}_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Report exported successfully')
    } catch (error) {
      // Fallback: Export as CSV from current data
      exportAsCSV()
    } finally {
      setIsExporting(false)
    }
  }

  const exportAsCSV = () => {
    // Generate CSV from current report data
    const csvData = [
      ['Report Period', period.replace('_', ' ')],
      ['Generated', new Date().toLocaleDateString()],
      [''],
      ['REVENUE SUMMARY'],
      ['This Month', data.revenue.thisMonth],
      ['Last Month', data.revenue.lastMonth],
      ['This Year', data.revenue.thisYear],
      ['Growth %', data.revenue.growth.toFixed(1)],
      [''],
      ['ORDERS SUMMARY'],
      ['Total Orders', data.orders.total],
      ['Completed', data.orders.completed],
      ['Pending', data.orders.pending],
      ['Avg Turnaround (days)', formatTurnaround(data.orders.avgTurnaround)],
      [''],
      ['COLLECTIONS'],
      ['This Month', data.collections.thisMonth],
      ['Outstanding', data.collections.outstanding],
      ['Overdue', data.collections.overdue],
      [''],
      ['TOP DOCTORS'],
      ['Name', 'Clinic', 'Revenue', 'Orders'],
      ...data.topDoctors.map(d => [d.name, d.clinicName, d.revenue, d.orders]),
      [''],
      ['CATEGORY BREAKDOWN'],
      ['Category', 'Revenue', 'Count'],
      ...data.categoryBreakdown.map(c => [c.category, c.revenue, c.count]),
    ]

    const csvContent = csvData.map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `report_${period}_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    toast.success('Report exported as CSV')
  }

  const { data: salesData, isLoading: salesLoading } = useQuery({
    queryKey: ['reports', 'sales', period],
    queryFn: async () => {
      const { data } = await api.get(`/reports/sales?period=${period}`)
      return data
    },
  })

  const { data: paymentsData, isLoading: paymentsLoading } = useQuery({
    queryKey: ['reports', 'payments', period],
    queryFn: async () => {
      const { data } = await api.get(`/reports/payments?period=${period}`)
      return data
    },
  })

  const { data: labProfile } = useQuery({
    queryKey: ['settings', 'lab-profile'],
    queryFn: async () => {
      const { data } = await api.get<LabProfile>('/settings/lab-profile')
      return data
    },
  })

  // Drill-down data queries
  const { data: drillDownData, isLoading: drillDownLoading } = useQuery({
    queryKey: ['reports', 'drill-down', drillDown?.type, period, drillDown?.doctorId, drillDown?.category],
    queryFn: async () => {
      if (!drillDown) return null

      let endpoint = ''
      const params: Record<string, string> = { period }

      switch (drillDown.type) {
        case 'revenue':
          endpoint = '/reports/drill-down/revenue'
          break
        case 'orders':
          endpoint = '/reports/drill-down/orders'
          break
        case 'collections':
          endpoint = '/reports/drill-down/collections'
          break
        case 'turnaround':
          endpoint = '/reports/drill-down/turnaround'
          break
        case 'doctor':
          endpoint = '/reports/drill-down/doctor'
          if (drillDown.doctorId) params.doctor_id = drillDown.doctorId
          break
        case 'category':
          endpoint = '/reports/drill-down/category'
          if (drillDown.category) params.category = drillDown.category
          break
        default:
          return null
      }

      const { data } = await api.get(endpoint, { params })
      return data
    },
    enabled: !!drillDown,
  })

  const isLoading = salesLoading || paymentsLoading

  const handlePrint = () => {
    window.print()
  }

  // Transform API data to report format
  const data: ReportData = {
    revenue: {
      thisMonth: salesData?.this_month_revenue ?? 0,
      lastMonth: salesData?.last_month_revenue ?? 0,
      thisYear: salesData?.this_year_revenue ?? 0,
      growth: salesData?.last_month_revenue 
        ? ((salesData?.this_month_revenue - salesData?.last_month_revenue) / salesData?.last_month_revenue * 100) 
        : 0,
    },
    orders: {
      total: salesData?.total_orders ?? 0,
      completed: salesData?.completed_orders ?? 0,
      pending: salesData?.pending_orders ?? 0,
      avgTurnaround: salesData?.avg_turnaround_days ?? 0,
    },
    collections: {
      thisMonth: paymentsData?.this_month_collections ?? 0,
      outstanding: paymentsData?.total_outstanding ?? 0,
      overdue: paymentsData?.overdue_amount ?? 0,
    },
    topDoctors: (salesData?.top_doctors ?? []).map((d: any) => ({
      id: d.id,
      name: d.name,
      clinicName: d.clinic_name ?? '-',
      revenue: d.revenue ?? 0,
      orders: d.order_count ?? 0,
    })),
    categoryBreakdown: (salesData?.category_breakdown ?? []).map((c: any) => ({
      category: c.category,
      revenue: c.revenue ?? 0,
      count: c.count ?? 0,
    })),
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  // Render monthly reports in their own tabs
  if (activeTab === 'monthly-sales') {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Business analytics and insights</p>
        </div>
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {REPORT_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <i className={`fas ${tab.icon}`}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        <MonthlySalesReport />
      </div>
    )
  }

  if (activeTab === 'monthly-sales-collection') {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Business analytics and insights</p>
        </div>
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {REPORT_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <i className={`fas ${tab.icon}`}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        <MonthlySalesCollectionReport />
      </div>
    )
  }

  return (
    <>
      {/* Print styles */}
      <style>{`
        @media print {
          .rpt-page { font-size:7.5pt; line-height:1.25; color:#111; page-break-after:always; }
          .rpt-page:last-child { page-break-after:auto; }
          .rpt-hdr { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:1.5pt solid #4f46e5; padding-bottom:4px; margin-bottom:6px; }
          .rpt-ftr { display:flex; justify-content:space-between; border-top:0.5pt solid #ccc; padding-top:3px; margin-top:12px; font-size:6.5pt; color:#888; }
          .rpt-tbl { width:100%; border-collapse:collapse; font-size:7pt; }
          .rpt-tbl th { background:#f3f4f6; font-weight:600; text-transform:uppercase; font-size:6.5pt; padding:2px 4px; border:0.5pt solid #ccc; text-align:left; }
          .rpt-tbl td { padding:2px 4px; border:0.5pt solid #ccc; }
          .rpt-tbl .r { text-align:right; }
          .rpt-grid { display:grid !important; grid-template-columns:repeat(4,1fr); gap:4px; margin-bottom:6px; }
          .rpt-card { border:0.5pt solid #ccc; border-radius:2px; padding:4px 6px; text-align:center; }
        }
      `}</style>

      {/* Screen View */}
      <div className="space-y-6 no-print">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Business analytics and insights
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex gap-1 -mb-px overflow-x-auto">
            {REPORT_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                <i className={`fas ${tab.icon}`}></i>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
          <div className="flex gap-3">
            <SearchableSelect
              value={period}
              onChange={setPeriod}
              searchable={false}
              icon="fa-calendar"
              options={[
                { value: 'this_month', label: 'This Month' },
                { value: 'last_month', label: 'Last Month' },
                { value: 'this_quarter', label: 'This Quarter' },
                { value: 'this_year', label: 'This Year' },
              ]}
            />
            <Button variant="secondary" onClick={handlePrint}>
              <i className="fas fa-print mr-2"></i>
              Print
            </Button>
            <Button variant="secondary" onClick={handleExport} loading={isExporting}>
              <i className="fas fa-download mr-2"></i>
              Export
            </Button>
          </div>
        </div>

      {/* Key Metrics - Clickable */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          className="cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-green-200 dark:hover:ring-green-800 transition-all"
          onClick={() => setDrillDown({ type: 'revenue', title: `Revenue Breakdown — ${getPeriodLabel(period)}` })}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
              <i className="fas fa-indian-rupee-sign text-xl text-green-600"></i>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">Revenue</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(data.revenue.thisMonth)}
              </p>
              <p className={`text-xs ${data.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                <i className={`fas fa-arrow-${data.revenue.growth >= 0 ? 'up' : 'down'} mr-1`}></i>
                {Math.abs(data.revenue.growth).toFixed(1)}% vs last month
              </p>
            </div>
            <i className="fas fa-chevron-right text-gray-400 text-sm"></i>
          </div>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-blue-200 dark:hover:ring-blue-800 transition-all"
          onClick={() => setDrillDown({ type: 'orders', title: `Orders Breakdown — ${getPeriodLabel(period)}` })}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <i className="fas fa-clipboard-list text-xl text-blue-600"></i>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">Orders</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{data.orders.total}</p>
              <p className="text-xs text-gray-500">
                {data.orders.completed} completed, {data.orders.pending} pending
              </p>
            </div>
            <i className="fas fa-chevron-right text-gray-400 text-sm"></i>
          </div>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-purple-200 dark:hover:ring-purple-800 transition-all"
          onClick={() => setDrillDown({ type: 'collections', title: `Collections Breakdown — ${getPeriodLabel(period)}` })}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <i className="fas fa-wallet text-xl text-purple-600"></i>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">Collections</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(data.collections.thisMonth)}
              </p>
              <p className="text-xs text-gray-500">
                {formatCurrency(data.collections.outstanding)} outstanding
              </p>
            </div>
            <i className="fas fa-chevron-right text-gray-400 text-sm"></i>
          </div>
        </Card>

        <Card
          className="cursor-pointer hover:shadow-lg hover:ring-2 hover:ring-amber-200 dark:hover:ring-amber-800 transition-all"
          onClick={() => setDrillDown({ type: 'turnaround', title: `Turnaround Details — ${getPeriodLabel(period)}` })}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
              <i className="fas fa-clock text-xl text-amber-600"></i>
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600 dark:text-gray-400">Avg. Turnaround</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {formatTurnaround(data.orders.avgTurnaround)}
              </p>
              <p className="text-xs text-gray-500">Per work order</p>
            </div>
            <i className="fas fa-chevron-right text-gray-400 text-sm"></i>
          </div>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Doctors - Clickable */}
        <Card>
          <CardHeader title="Top Doctors by Revenue" subtitle={getPeriodLabel(period)} />
          <div className="space-y-2">
            {data.topDoctors.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No data for this period</p>
            ) : (
              data.topDoctors.map((doctor, index) => (
                <div
                  key={doctor.id}
                  className="flex items-center gap-4 p-2 -mx-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  onClick={() => setDrillDown({
                    type: 'doctor',
                    title: `${doctor.name} — Orders Breakdown`,
                    doctorId: doctor.id,
                  })}
                >
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center text-sm font-bold text-primary-600">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{doctor.name}</p>
                    <p className="text-sm text-gray-500">{doctor.clinicName}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {formatCurrency(doctor.revenue)}
                    </p>
                    <p className="text-xs text-gray-500">{doctor.orders} orders</p>
                  </div>
                  <i className="fas fa-chevron-right text-gray-400 text-xs"></i>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Category Breakdown - Clickable */}
        <Card>
          <CardHeader title="Revenue by Category" subtitle={getPeriodLabel(period)} />
          <div className="space-y-4">
            {data.categoryBreakdown.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No data for this period</p>
            ) : (
              data.categoryBreakdown.map((cat) => {
                const totalRevenue = data.categoryBreakdown.reduce((sum, c) => sum + c.revenue, 0)
                const percentage = totalRevenue > 0 ? (cat.revenue / totalRevenue) * 100 : 0
                return (
                  <div
                    key={cat.category}
                    className="cursor-pointer p-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    onClick={() => setDrillDown({
                      type: 'category',
                      title: `${cat.category} — Order Details`,
                      category: cat.category,
                    })}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {cat.category}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {formatCurrency(cat.revenue)} ({cat.count})
                        </span>
                        <i className="fas fa-chevron-right text-gray-400 text-xs"></i>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </Card>
      </div>

    </div>

      {/* ── PRINT-ONLY LAYOUT ────────────────────────────────────── */}
      <div className="print-only hidden">
        <div className="rpt-page">
          {/* Header */}
          <div className="rpt-hdr">
            <div style={{display:'flex',alignItems:'center',gap:'6px'}}>
              <div style={{width:24,height:24,background:'#4f46e5',borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <i className="fas fa-tooth" style={{color:'#fff',fontSize:'10px'}}></i>
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:'9pt'}}>{labProfile?.name ?? 'Jasmine Dental Lab'}</div>
                <div style={{fontSize:'6.5pt',color:'#666'}}>{labProfile?.phone}{labProfile?.email ? ` | ${labProfile.email}` : ''}</div>
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:'8pt',fontWeight:700,color:'#4f46e5'}}>BUSINESS REPORT</div>
              <div style={{fontSize:'8pt',fontWeight:700}}>{getPeriodLabel(period)}</div>
              <div style={{fontSize:'6.5pt',color:'#666'}}>Generated: {new Date().toLocaleDateString()}</div>
            </div>
          </div>

          {/* Key Metrics */}
          <div style={{fontSize:'6pt',fontWeight:600,textTransform:'uppercase',color:'#888',marginBottom:3}}>Key Metrics</div>
          <div className="rpt-grid">
            <div className="rpt-card">
              <div style={{fontSize:'9pt',fontWeight:700,color:'#16a34a'}}>{formatCurrency(data.revenue.thisMonth)}</div>
              <div style={{fontSize:'6pt',color:'#888'}}>Revenue</div>
              <div style={{fontSize:'6pt',color:'#aaa'}}>{data.revenue.growth >= 0 ? '+' : ''}{data.revenue.growth.toFixed(1)}% vs last month</div>
            </div>
            <div className="rpt-card">
              <div style={{fontSize:'10pt',fontWeight:700}}>{data.orders.total}</div>
              <div style={{fontSize:'6pt',color:'#888'}}>Orders</div>
              <div style={{fontSize:'6pt',color:'#aaa'}}>{data.orders.completed} done / {data.orders.pending} pending</div>
            </div>
            <div className="rpt-card">
              <div style={{fontSize:'9pt',fontWeight:700,color:'#7c3aed'}}>{formatCurrency(data.collections.thisMonth)}</div>
              <div style={{fontSize:'6pt',color:'#888'}}>Collections</div>
              <div style={{fontSize:'6pt',color:'#aaa'}}>{formatCurrency(data.collections.outstanding)} outstanding</div>
            </div>
            <div className="rpt-card">
              <div style={{fontSize:'9pt',fontWeight:700,color:'#d97706'}}>{formatTurnaround(data.orders.avgTurnaround)}</div>
              <div style={{fontSize:'6pt',color:'#888'}}>Avg. Turnaround</div>
            </div>
          </div>

          {/* Revenue & Collections side by side */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:8}}>
            <div>
              <div style={{fontSize:'6pt',fontWeight:600,textTransform:'uppercase',color:'#888',marginBottom:2}}>Revenue Summary</div>
              <table className="rpt-tbl">
                <tbody>
                  <tr><td style={{fontWeight:500}}>This Month</td><td className="r" style={{fontWeight:600}}>{formatCurrency(data.revenue.thisMonth)}</td></tr>
                  <tr><td>Last Month</td><td className="r">{formatCurrency(data.revenue.lastMonth)}</td></tr>
                  <tr><td>This Year (YTD)</td><td className="r">{formatCurrency(data.revenue.thisYear)}</td></tr>
                  <tr><td>MoM Growth</td><td className="r" style={{fontWeight:600,color:data.revenue.growth >= 0 ? '#16a34a' : '#dc2626'}}>{data.revenue.growth >= 0 ? '+' : ''}{data.revenue.growth.toFixed(1)}%</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <div style={{fontSize:'6pt',fontWeight:600,textTransform:'uppercase',color:'#888',marginBottom:2}}>Collections Summary</div>
              <table className="rpt-tbl">
                <tbody>
                  <tr><td>This Month</td><td className="r" style={{fontWeight:600,color:'#16a34a'}}>{formatCurrency(data.collections.thisMonth)}</td></tr>
                  <tr><td>Outstanding</td><td className="r" style={{color:'#d97706'}}>{formatCurrency(data.collections.outstanding)}</td></tr>
                  <tr><td>Overdue</td><td className="r" style={{fontWeight:600,color:'#dc2626'}}>{formatCurrency(data.collections.overdue)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Doctors */}
          {data.topDoctors.length > 0 && (
            <div style={{marginBottom:8}}>
              <div style={{fontSize:'6pt',fontWeight:600,textTransform:'uppercase',color:'#888',marginBottom:2}}>Top Doctors by Revenue</div>
              <table className="rpt-tbl">
                <thead><tr><th>#</th><th>Doctor</th><th>Clinic</th><th className="r">Revenue</th><th className="r">Orders</th></tr></thead>
                <tbody>
                  {data.topDoctors.map((doc, idx) => (
                    <tr key={doc.id}><td>{idx+1}</td><td style={{fontWeight:500}}>{doc.name}</td><td>{doc.clinicName}</td><td className="r" style={{fontWeight:600}}>{formatCurrency(doc.revenue)}</td><td className="r">{doc.orders}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Category Breakdown */}
          {data.categoryBreakdown.length > 0 && (
            <div>
              <div style={{fontSize:'6pt',fontWeight:600,textTransform:'uppercase',color:'#888',marginBottom:2}}>Revenue by Category</div>
              <table className="rpt-tbl">
                <thead><tr><th>Category</th><th className="r">Revenue</th><th className="r">Items</th><th className="r">% Share</th></tr></thead>
                <tbody>
                  {data.categoryBreakdown.map((cat) => {
                    const totalRev = data.categoryBreakdown.reduce((s, c) => s + c.revenue, 0)
                    const pct = totalRev > 0 ? (cat.revenue / totalRev) * 100 : 0
                    return <tr key={cat.category}><td style={{fontWeight:500}}>{cat.category}</td><td className="r">{formatCurrency(cat.revenue)}</td><td className="r">{cat.count}</td><td className="r">{pct.toFixed(1)}%</td></tr>
                  })}
                </tbody>
                <tfoot>
                  <tr style={{fontWeight:700,background:'#f3f4f6'}}>
                    <td>TOTAL</td>
                    <td className="r">{formatCurrency(data.categoryBreakdown.reduce((s, c) => s + c.revenue, 0))}</td>
                    <td className="r">{data.categoryBreakdown.reduce((s, c) => s + c.count, 0)}</td>
                    <td className="r">100%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="rpt-ftr">
            <span>{labProfile?.address}</span>
            <span style={{fontWeight:600}}>Business Report &mdash; {getPeriodLabel(period)} &nbsp;|&nbsp; Page 1 of 1</span>
            <span>Printed: {new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Drill-Down Modal */}
      <Modal
        isOpen={!!drillDown}
        onClose={closeDrillDown}
        title={drillDown?.title || 'Details'}
        size="xl"
      >
        {drillDownLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : drillDownData ? (
          <div className="space-y-4">
            {/* Summary row if available */}
            {drillDownData.summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {Object.entries(drillDownData.summary as Record<string, { label: string; value: string | number; color?: string }>).map(([key, item]) => (
                  <div key={key} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{item.label}</p>
                    <p className={`text-lg font-bold ${item.color || 'text-gray-900 dark:text-white'}`}>
                      {typeof item.value === 'number' && (key.toLowerCase().includes('amount') || key.toLowerCase().includes('revenue') || key.toLowerCase().includes('total'))
                        ? formatCurrency(item.value as number)
                        : item.value}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Data table */}
            {drillDownData.items && drillDownData.items.length > 0 ? (
              <div className="overflow-x-auto max-h-96">
                <table className="table text-sm">
                  <thead className="sticky top-0 bg-white dark:bg-gray-800">
                    <tr>
                      <th className="w-10">#</th>
                      {drillDownData.columns?.map((col: { key: string; label: string; align?: string }) => (
                        <th key={col.key} className={col.align === 'right' ? 'text-right' : ''}>
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {drillDownData.items.map((item: Record<string, unknown>, idx: number) => (
                      <tr key={idx}>
                        <td className="text-gray-500">{idx + 1}</td>
                        {drillDownData.columns?.map((col: { key: string; format?: string; align?: string }) => (
                          <td key={col.key} className={col.align === 'right' ? 'text-right' : ''}>
                            {col.format === 'currency'
                              ? formatCurrency(item[col.key] as number)
                              : col.format === 'date'
                              ? new Date(item[col.key] as string).toLocaleDateString('en-IN')
                              : String(item[col.key] ?? '-')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  {drillDownData.totals && (
                    <tfoot>
                      <tr className="bg-gray-50 dark:bg-gray-700/50 font-bold">
                        <td></td>
                        {drillDownData.columns?.map((col: { key: string; format?: string; align?: string }) => (
                          <td key={col.key} className={col.align === 'right' ? 'text-right' : ''}>
                            {drillDownData.totals[col.key] !== undefined
                              ? col.format === 'currency'
                                ? formatCurrency(drillDownData.totals[col.key])
                                : drillDownData.totals[col.key]
                              : ''}
                          </td>
                        ))}
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <i className="fas fa-inbox text-3xl mb-2 opacity-50"></i>
                <p>No detailed data available for this period</p>
              </div>
            )}

            {/* Period info */}
            <div className="text-xs text-gray-400 text-right pt-2 border-t border-gray-200 dark:border-gray-700">
              Period: {getPeriodLabel(period)} | Generated: {new Date().toLocaleString('en-IN')}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <i className="fas fa-exclamation-circle text-3xl mb-2 opacity-50"></i>
            <p>Unable to load data. Please try again.</p>
          </div>
        )}
      </Modal>
    </>
  )
}
